import { toast } from 'sonner';
import { X, WhatsappLogo, XLogo, Copy, Download, ShareNetwork } from '@phosphor-icons/react';
import { isNativeApp } from '../lib/pwa';
import Modal from './Modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Share-card modal shown after a fan-engagement action (investment / backstage purchase).
 *
 * Props:
 *  - open: bool
 *  - onClose: fn
 *  - movieId, movieTitle
 *  - name: user's display name
 *  - amountInr: optional
 *  - tier: optional ("FAN" | "SUPER" | "PATRON")
 *  - referralCode: optional — embedded in the share URL
 */
const ShareCardModal = ({ open, onClose, movieId, movieTitle, name, amountInr, tier, referralCode }) => {
  if (!open) return null;

  const cardParams = new URLSearchParams({
    ...(name ? { name } : {}),
    ...(amountInr ? { amount_inr: String(amountInr) } : {}),
    ...(tier ? { tier } : {}),
  });
  const cardUrl = `${BACKEND_URL}/api/social-card/movie/${movieId}?${cardParams.toString()}`;

  const movieLink = `${window.location.origin}/movie/${movieId}${referralCode ? `?ref=${referralCode}` : ''}`;
  const msg = tier
    ? `Just unlocked the ${tier} backstage pass for "${movieTitle}" on Fanzingy! Join me — ${movieLink}`
    : amountInr
    ? `Just backed ₹${Number(amountInr).toLocaleString('en-IN')} into "${movieTitle}" on Fanzingy. Be part of the next box-office hit. ${movieLink}`
    : `Just discovered "${movieTitle}" on Fanzingy. Worth a look! ${movieLink}`;

  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`, '_blank');
  const copyLink = () => { navigator.clipboard.writeText(movieLink); toast.success('Link copied'); };
  const download = () => {
    const a = document.createElement('a');
    a.href = cardUrl;
    a.download = `fanzingy-${movieId.slice(0, 8)}.png`;
    a.click();
  };
  const shareNative = async () => {
    // Inside the Capacitor wrapper, use the native iOS / Android share sheet.
    if (isNativeApp()) {
      try {
        const { Share } = await import('@capacitor/share');
        await Share.share({ title: 'Fanzingy', text: msg, url: movieLink, dialogTitle: 'Share with…' });
        return;
      } catch (_) { /* fall through */ }
    }
    if (navigator.share) {
      try { await navigator.share({ title: 'Fanzingy', text: msg, url: movieLink }); }
      catch (_) {}
    } else copyLink();
  };

  return (
    <Modal onClose={onClose} title="Share this movie" testid="share-card-modal">
      <div className="bg-[#0d0820] border border-white/15 rounded-2xl max-w-md w-full p-6 sm:p-7 relative">
        <button
          onClick={onClose}
          data-testid="share-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 text-white/70 hover:text-white"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="text-[11px] uppercase tracking-wider text-[#facc15] font-bold mb-1">Spread the word</div>
        <h3 className="text-xl font-extrabold mb-1 text-white">Tell the world.</h3>
        <p className="text-xs text-white/70 mb-4 font-light">Drop this card into WhatsApp / Insta Stories / X. Looks gorgeous everywhere.</p>

        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black/40 aspect-square mb-4" data-testid="share-card-preview">
          <img src={cardUrl} alt={`Share card for ${movieTitle}`} className="w-full h-full object-cover" />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <button onClick={shareWA} data-testid="share-modal-whatsapp" className="bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/40 text-[#25D366] text-xs font-bold rounded-lg px-3 py-2.5 flex items-center justify-center gap-1.5">
            <WhatsappLogo size={14} weight="bold" /> WhatsApp
          </button>
          <button onClick={shareX} data-testid="share-modal-x" className="bg-white/10 hover:bg-white/15 border border-white/20 text-white text-xs font-bold rounded-lg px-3 py-2.5 flex items-center justify-center gap-1.5">
            <XLogo size={14} weight="bold" /> X / Twitter
          </button>
          <button onClick={copyLink} data-testid="share-modal-copy" className="bg-white/8 hover:bg-white/15 border border-white/15 text-white/80 text-xs font-bold rounded-lg px-3 py-2.5 flex items-center justify-center gap-1.5">
            <Copy size={14} weight="bold" /> Copy link
          </button>
          <button onClick={download} data-testid="share-modal-download" className="bg-white/8 hover:bg-white/15 border border-white/15 text-white/80 text-xs font-bold rounded-lg px-3 py-2.5 flex items-center justify-center gap-1.5">
            <Download size={14} weight="bold" /> Download
          </button>
        </div>
        <button onClick={shareNative} data-testid="share-modal-native" className="w-full mt-1 bg-gradient-to-r from-[#f472b6] via-[#c084fc] to-[#22d3ee] text-black text-sm font-extrabold rounded-lg py-3 flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
          <ShareNetwork size={16} weight="bold" /> Share via…
        </button>
      </div>
    </Modal>
  );
};

export default ShareCardModal;
